import type { ReactNode } from "react";

import { Button } from "@/components/ui";
import { t } from "@/i18n";

/** Tasarim sistemi form alani — `.input` (index.css `@layer components`),
 * bkz. docs/arayuz-tasarim-rehberi.md §1. */
export const inputClass = "input";

interface FieldShellProps {
  label: string;
  settingKey?: string;
  error?: string | null;
  onReset?: () => void;
  hint?: string;
  children: ReactNode;
}

/** Ortak alan kabugu: etiket + (opsiyonel) "Varsayılana dön" + 422 alan
 * hatasi. `data-field` niteligi testlerin/gorsel denetimin alani bulmasi
 * icindir (gorev talimati madde 2: "422 alan hataları"). */
export function FieldShell({ label, settingKey, error, onReset, hint, children }: FieldShellProps) {
  return (
    <div data-field={settingKey} className="space-y-1">
      <div className="flex items-center justify-between gap-2">
        <label htmlFor={settingKey} className="block text-sm font-medium text-ink">
          {label}
        </label>
        {onReset && (
          <Button type="button" variant="link" size="sm" onClick={onReset} className="!min-h-[36px]">
            {t("ayarlar.reset_field")}
          </Button>
        )}
      </div>
      {children}
      {hint && <p className="text-xs text-muted">{hint}</p>}
      {error && (
        <p role="alert" className="text-xs text-red">
          {error}
        </p>
      )}
    </div>
  );
}

interface TextFieldProps {
  label: string;
  settingKey: string;
  value: string;
  onChange: (value: string) => void;
  onReset?: () => void;
  error?: string | null;
  hint?: string;
  type?: "text" | "number" | "color" | "url";
  disabled?: boolean;
  placeholder?: string;
}

export function TextField({
  label,
  settingKey,
  value,
  onChange,
  onReset,
  error,
  hint,
  type = "text",
  disabled,
  placeholder,
}: TextFieldProps) {
  return (
    <FieldShell label={label} settingKey={settingKey} error={error} onReset={onReset} hint={hint}>
      <div className="flex items-center gap-2">
        <input
          id={settingKey}
          type={type}
          value={value}
          disabled={disabled}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          className={type === "color" ? "h-11 w-16 rounded-[9px] border-[1.5px] border-line bg-card p-1" : inputClass}
        />
        {type === "color" && <code className="text-xs text-muted">{value}</code>}
      </div>
    </FieldShell>
  );
}

interface NumberFieldProps {
  label: string;
  settingKey: string;
  value: number | null;
  onChange: (value: number | null) => void;
  onReset?: () => void;
  error?: string | null;
  min?: number;
}

export function NumberField({ label, settingKey, value, onChange, onReset, error, min }: NumberFieldProps) {
  return (
    <FieldShell label={label} settingKey={settingKey} error={error} onReset={onReset}>
      <input
        id={settingKey}
        type="number"
        min={min}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
        className={inputClass}
      />
    </FieldShell>
  );
}

interface ToggleFieldProps {
  label: string;
  settingKey: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

export function ToggleField({ label, settingKey, checked, onChange }: ToggleFieldProps) {
  return (
    <label className="flex min-h-[44px] items-center gap-2 text-sm text-ink" data-field={settingKey}>
      <input
        id={settingKey}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4"
      />
      {label}
    </label>
  );
}
